import React from 'react';
import { Link } from 'react-router-dom';
import { FileText, Download, Shield, Globe, Clock, Users, CheckCircle, ArrowRight } from 'lucide-react';
import AdBanner from '../components/Layout/AdBanner';

const Home: React.FC = () => {
  const features = [
    {
      icon: FileText,
      title: 'Professional Templates',
      description: 'Create stunning invoices with our professionally designed templates'
    },
    {
      icon: Download,
      title: 'Instant PDF Download',
      description: 'Generate and download your invoices as PDF files in seconds'
    },
    {
      icon: Shield,
      title: 'Secure & Private',
      description: 'Your data is encrypted and stored securely with enterprise-grade security'
    },
    {
      icon: Globe,
      title: 'Multi-Currency Support',
      description: 'Support for USD, EUR, GBP, BRL, INR and many more currencies'
    },
    {
      icon: Clock,
      title: 'Save Time',
      description: 'Create invoices in minutes, not hours. Focus on growing your business'
    },
    {
      icon: Users,
      title: 'Trusted by 50,000+',
      description: 'Join thousands of freelancers and businesses who trust our platform'
    }
  ];

  const benefits = [
    'No registration required for basic use',
    'Unlimited invoice generation',
    'Email delivery to clients',
    'Professional invoice templates',
    'Mobile-responsive design',
    'Multiple currency support',
    'Tax calculation included',
    'Cloud storage with account'
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Hero Section */}
      <section className="pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Create Professional 
              <span className="text-blue-600"> Invoices</span>
              <br />in Seconds
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              The most trusted free invoice generator for businesses worldwide. Create, download, and send professional invoices instantly.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/generate-invoice"
                className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2 hover:shadow-lg"
              >
                <FileText className="h-6 w-6" />
                <span>Generate Invoice Now</span>
              </Link>
              <Link
                to="/blog/invoice-generator-tutorial"
                className="border-2 border-blue-600 text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-600 hover:text-white transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <span>How It Works</span>
                <ArrowRight className="h-5 w-5" />
              </Link>
            </div>
            
            {/* Template Preview */}
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-6">Choose from 10+ professional templates</p>
              <div className="flex justify-center space-x-4 overflow-x-auto pb-4">
                <div className="flex-shrink-0 w-24 h-32 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg shadow-lg"></div>
                <div className="flex-shrink-0 w-24 h-32 bg-gradient-to-br from-green-600 to-emerald-700 rounded-lg shadow-lg"></div>
                <div className="flex-shrink-0 w-24 h-32 bg-gradient-to-br from-purple-600 to-purple-700 rounded-lg shadow-lg"></div>
                <div className="flex-shrink-0 w-24 h-32 bg-gradient-to-br from-orange-600 to-red-600 rounded-lg shadow-lg"></div>
                <div className="flex-shrink-0 w-24 h-32 bg-gradient-to-br from-teal-600 to-cyan-600 rounded-lg shadow-lg"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Everything You Need to Create Perfect Invoices
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our invoice generator includes all the features you need to create professional invoices and get paid faster.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-gray-50 p-8 rounded-xl hover:shadow-lg transition-all duration-200 hover:-translate-y-1">
                <feature.icon className="h-12 w-12 text-blue-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why Choose InvoiceGen?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                We've built the most comprehensive and user-friendly invoice generator to help you create professional invoices without any hassle.
              </p>
              
              <div className="grid grid-cols-1 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                    <span className="text-gray-700">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Start Creating Invoices Today</h3>
              <p className="text-gray-600 mb-6">
                Join thousands of freelancers and businesses who trust InvoiceGen for their invoicing needs.
              </p>
              <Link
                to="/generate-invoice"
                className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-all duration-200 flex items-center justify-center space-x-2"
              >
                <FileText className="h-5 w-5" />
                <span>Create Your First Invoice</span>
              </Link>
              
              <div className="mt-6 text-center">
                <p className="text-sm text-gray-500">
                  No registration required • Free forever
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Ad Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AdBanner size="responsive" className="mx-auto" />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Get Paid Faster?
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            Create your first professional invoice in under 2 minutes. No credit card required.
          </p>
          <Link
            to="/generate-invoice"
            className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-all duration-200 inline-flex items-center space-x-2 hover:shadow-lg"
          >
            <FileText className="h-6 w-6" />
            <span>Start Creating Invoices</span>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;